/**
 * Timesheet sheet — one employee, one pay period, laid out like HR's Excel timesheets.
 * Backend: GET /attendance/sheets/{employee}/{YYYY-MM} (HR, managers) and /attendance/my/sheets/{YYYY-MM}
 * (the employee). Every value comes from the server; a value the profile lacks shows "—" with a marker.
 * Print uses the browser; "Excel" downloads the server's export, built from the same payload.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Printer, FileSpreadsheet, Loader2, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { API_URL, TS_STATUS, errorText, hm, label } from './shared';

const money = (v) => (v === null || v === undefined ? null
  : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
// payable overtime is ×1.5, so it can end in half a minute: shown with seconds, as the Excel export does
const hms = (minutes) => (Number.isInteger(minutes) ? hm(minutes)
  : `${Math.floor(minutes / 60)}:${String(Math.floor(minutes % 60)).padStart(2, '0')}:${String(Math.round((minutes % 1) * 60)).padStart(2, '0')}`);

const FIELD_LABEL = {
  'employment.employee_number': ['Employee No.', 'الرقم الوظيفي'],
  'personal.first_name/last_name': ['Name', 'الاسم'],
  'personal.first_name_ar/last_name_ar': ['Name (Arabic)', 'الاسم بالعربية'],
  'employment.status': ['Employment status', 'حالة التوظيف'],
  'employment.contract_type': ['Contract type', 'نوع العقد'],
  'employment.branch_id': ['Branch', 'الفرع'],
  'employment.department_id': ['Department', 'القسم'],
  'employment.job_title': ['Job title', 'المسمى الوظيفي'],
  'employment.work_patterns': ['Weekly work pattern (off days)', 'نمط العمل الأسبوعي (أيام الراحة)'],
  'compensation.basic_salary': ['Basic salary', 'الراتب الأساسي'],
  'compensation.allowances': ['Allowances', 'البدلات'],
};

// The header's work-pattern line: the text comes from the server, the same the Excel export writes.
// The company weekend is never shown as the employee's contract.
export function patternLine(wp, lang) {
  return <span className={wp.is_default ? 'text-amber-700' : ''}>{wp.text?.[lang]}</span>;
}

function Missing({ why }) {
  return (
    <span className="text-amber-700 font-sans" title={why}>
      — <span className="text-[10px] uppercase tracking-wide">{why}</span>
    </span>
  );
}

export default function TimesheetSheet({ token, isRTL, data, exportPath }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const lang = isRTL ? 'ar' : 'en';
  const [downloading, setDownloading] = useState(false);
  const missingProfile = t('missing in profile', 'غير موجود في الملف');
  const { period, employee: e, rows, totals: tot, approval: ap, pay } = data;
  const named = (v) => (v ? (isRTL ? v.ar || v.en : v.en || v.ar) : null);
  const value = (v) => (v ? <span>{v}</span> : <Missing why={missingProfile} />);

  const download = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API_URL}${exportPath}`, {
        headers: { Authorization: `Bearer ${token}` }, params: { lang }, responseType: 'blob' });
      const name = /filename="([^"]+)"/.exec(r.headers['content-disposition'] || '')?.[1] || `timesheet-${period.key}.xlsx`;
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = name; a.click();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(errorText(err)); } finally { setDownloading(false); }
  };

  const time = (p, which) => {
    if (!p) return '';
    const v = p[which];
    if (!v) return <span className="text-amber-700">—</span>;
    return (
      <span className={p.added ? 'underline decoration-dotted' : ''} title={p.added ? t('added by a fix or an approved request', 'أضيفت بتصحيح أو طلب معتمد') : undefined}>
        {v}{p[`${which}_next_day`] ? <sup className="text-slate-400"> +1</sup> : null}
      </span>
    );
  };

  const who = (step) => {
    if (!step) return <span className="text-slate-400">—</span>;
    const by = step.by || {};
    const name = by.name || (by.automatic ? t('automatic (review window passed)', 'تلقائي (انتهت مهلة المراجعة)') : by.id);
    return <span>{name} <span className="text-slate-500 text-xs">{step.at_local}</span></span>;
  };

  const dm = ap.direct_manager || {};
  const mgr = dm.manager;
  return (
    <div className="space-y-4" data-testid="timesheet-sheet">
      <style>{`@media print {
        html, body, main, #root { background: #fff !important; }
        body * { visibility: hidden; }
        [data-print-area], [data-print-area] * { visibility: visible; }
        [data-print-area] { position: absolute; left: 0; top: 0; width: 100%; }
        .no-print { display: none !important; }
        @page { size: A4 portrait; margin: 10mm; }
      }`}</style>

      <div className="flex flex-wrap items-center gap-2 no-print">
        <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="sheet-print">
          <Printer className="h-4 w-4 me-1" />{t('Print', 'طباعة')}
        </Button>
        <Button size="sm" variant="outline" onClick={download} disabled={downloading} data-testid="sheet-export">
          {downloading ? <Loader2 className="h-4 w-4 me-1 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 me-1" />}
          {t('Export to Excel', 'تصدير إلى Excel')}
        </Button>
      </div>

      <div data-print-area dir={isRTL ? 'rtl' : 'ltr'} className="bg-white text-[13px] space-y-3">
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 border-b pb-2">
          <h2 className="text-lg font-bold">{t('Monthly Time Sheet', 'كشف الدوام الشهري')}</h2>
          <span data-testid="sheet-period">
            {t('Pay period', 'فترة الراتب')} <b>{period.key}</b>: {period.start} – {period.end} ({period.days} {t('days', 'يوماً')})
          </span>
          <span className="text-xs rounded px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200">
            {t('Period and rate: to confirm with payroll', 'الفترة ومعادلة الأجر: بانتظار تأكيد الرواتب')}
          </span>
          <span className={`text-xs rounded-full px-2 py-0.5 ${TS_STATUS[ap.status]?.cls || ''}`}>{label(TS_STATUS, ap.status, isRTL)}</span>
          <span className="text-xs text-slate-500" data-testid="sheet-source">
            {data.source === 'approved' ? t('As approved by HR', 'كما اعتمدته الموارد البشرية') : t('Live — not approved yet', 'مباشر — لم يُعتمد بعد')}
          </span>
        </div>
        {(period.gap_days > 0 || period.overlap_days > 0) && (
          <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            <AlertTriangle className="h-4 w-4" />
            {period.gap_days > 0
              ? t(`The pay period setting changed: ${period.gap_days} day(s) before this period are on no timesheet.`,
                `تغيّر إعداد فترة الراتب: ${period.gap_days} يوم قبل هذه الفترة لا تظهر في أي كشف.`)
              : t(`The pay period setting changed: ${period.overlap_days} day(s) are also on the previous timesheet.`,
                `تغيّر إعداد فترة الراتب: ${period.overlap_days} يوم تظهر أيضاً في الكشف السابق.`)}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1" data-testid="sheet-header">
          <div><span className="text-slate-500">{t('Employee No.', 'الرقم الوظيفي')}: </span>{value(e.employee_number)}</div>
          <div><span className="text-slate-500">{t('Name', 'الاسم')}: </span>{value(e.name)}</div>
          <div><span className="text-slate-500">{t('Name (Arabic)', 'الاسم بالعربية')}: </span>{value(e.name_ar)}</div>
          <div><span className="text-slate-500">{t('Employment status', 'حالة التوظيف')}: </span>{value(e.status)}</div>
          <div><span className="text-slate-500">{t('Contract type', 'نوع العقد')}: </span>{value(e.contract_type)}{e.employment_type ? ` · ${e.employment_type}` : ''}</div>
          <div><span className="text-slate-500">{t('Branch', 'الفرع')}: </span>{value(named(e.branch))}</div>
          <div><span className="text-slate-500">{t('Department', 'القسم')}: </span>{value(named(e.department))}</div>
          <div><span className="text-slate-500">{t('Job title', 'المسمى الوظيفي')}: </span>{value(named(e.job_title))}</div>
        </div>
        {e.work_pattern && (
          <div data-testid="sheet-work-pattern">
            <span className="text-slate-500">{t('Work pattern', 'نمط العمل الأسبوعي')}: </span>
            {patternLine(e.work_pattern, lang)}
          </div>
        )}
        {e.missing?.length > 0 && (
          <div className="text-xs text-amber-800 no-print" data-testid="sheet-missing">
            {t('Missing in the employee profile: ', 'غير موجود في ملف الموظف: ')}
            {e.missing.map((m) => (FIELD_LABEL[m] ? FIELD_LABEL[m][isRTL ? 1 : 0] : m)).join('، ')}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-center" data-testid="sheet-table">
            <thead className="bg-slate-100">
              <tr>
                {[['Date', 'التاريخ'], ['Day', 'اليوم'], ['Time In', 'دخول'], ['Time Out', 'خروج'], ['Time In', 'دخول'], ['Time Out', 'خروج'],
                  ['Regular Hours', 'الساعات الأساسية'], ['OT', 'الإضافي'], ['Total Working Hours', 'مجموع ساعات العمل'],
                  ['Time Diff', 'النقص'], ['Remarks', 'ملاحظات']].map(([en, ar], i) => (
                  <th key={i} className="border px-1.5 py-1 font-semibold">{t(en, ar)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.date} data-testid="sheet-row" data-date={r.date}
                  className={r.status === 'not_employed' || r.status === 'future' ? 'text-slate-400' : r.day_type !== 'work' ? 'bg-yellow-50/50' : ''}>
                  <td className="border px-1.5 py-0.5 font-mono text-xs whitespace-nowrap">{r.date}</td>
                  <td className="border px-1.5 py-0.5 text-xs whitespace-nowrap" data-col="weekday">{r.weekday_name?.[lang]}</td>
                  {[0, 1].map((i) => (
                    <React.Fragment key={i}>
                      <td className="border px-1.5 py-0.5 font-mono">{time(r.pairs[i], 'in')}</td>
                      <td className="border px-1.5 py-0.5 font-mono">{time(r.pairs[i], 'out')}</td>
                    </React.Fragment>
                  ))}
                  <td className="border px-1.5 py-0.5 font-mono" data-col="regular">{hm(r.regular_minutes)}</td>
                  <td className="border px-1.5 py-0.5 font-mono" data-col="ot">{hm(r.overtime_minutes)}</td>
                  <td className="border px-1.5 py-0.5 font-mono" data-col="worked">{hm(r.worked_minutes)}</td>
                  <td className={`border px-1.5 py-0.5 font-mono ${r.shortfall_minutes ? 'text-rose-700' : ''}`} data-col="diff">{hm(r.shortfall_minutes)}</td>
                  <td className="border px-1.5 py-0.5 text-start text-xs">
                    {r.remarks.map((m, i) => (
                      <React.Fragment key={i}>
                        {i > 0 && ' · '}
                        {/* a warning reference opens its details; plain text when printed */}
                        {m.link ? <a href={m.link} className="text-blue-700 underline print:text-inherit print:no-underline"
                          data-testid={`remark-link-${r.date}`}>{m.text[lang]}</a> : m.text[lang]}
                      </React.Fragment>
                    ))}
                    {r.more_pairs.length > 0 && (
                      <span className="text-slate-500"> · {t('more punches', 'بصمات إضافية')}: {r.more_pairs.map((p) => `${p.in || '—'}–${p.out || '—'}`).join(', ')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="sheet-totals">
          {[
            ['days_worked', 'Days worked', 'أيام العمل', (v) => v],
            ['overtime_minutes', 'Total OT', 'مجموع الإضافي', hm],
            ['payable_overtime_minutes', 'Payable OT (×1.5)', 'الإضافي المستحق (×1.5)', hms],
            ['worked_minutes', 'Total working hours', 'مجموع ساعات العمل', hm],
            ['required_minutes', 'Required hours', 'الساعات المطلوبة', hm],
            ['shortfall_minutes', 'Shortfall', 'النقص', hm],
            ['absent_unexcused_days', 'Unexcused absence (days)', 'غياب بدون عذر (أيام)', (v) => v],
            ['leave_days', 'Leave (days)', 'إجازة (أيام)', (v) => v],
          ].map(([k, en, ar, fmt]) => (
            <div key={k} className="border rounded px-2 py-1" data-total={k}>
              <div className="text-[11px] text-slate-500">{t(en, ar)}</div>
              <div className="font-semibold font-mono">{fmt(tot[k])}</div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-slate-500">
          {t('Time diff is the shortfall of each working day (base − worked, never below 0); overtime is counted separately and never cancels it. A whole day of unexcused absence is counted in days. Underlined times were added by a fix or an approved request; +1 is the next day.',
            'النقص هو نقص كل يوم عمل (الأساس − العمل، ولا يقل عن صفر)، ويُحسب الإضافي منفصلاً ولا يلغيه. يُحسب يوم الغياب الكامل بدون عذر بالأيام. الأوقات المسطّرة أُضيفت بتصحيح أو طلب معتمد؛ ‎+1 تعني اليوم التالي.')}
        </p>

        <div className="border rounded p-2 grid md:grid-cols-2 gap-x-6 gap-y-1" data-testid="sheet-approval">
          <div className="md:col-span-2 font-semibold">{t('Approval', 'الاعتماد')} <span className="text-xs font-normal text-slate-500">({t('Riyadh time', 'بتوقيت الرياض')})</span></div>
          <div>
            <span className="text-slate-500">{t('Direct manager (profile)', 'المدير المباشر (الملف)')}: </span>
            {mgr ? (isRTL ? mgr.name_ar || mgr.name : mgr.name || mgr.name_ar) : <Missing why={missingProfile} />}
          </div>
          <div data-testid="sheet-manager-approval">
            <span className="text-slate-500">{t('Direct manager approval', 'اعتماد المدير المباشر')}: </span>
            {who(dm.signed)}
            {dm.signed?.on_behalf && (
              <span className="text-xs text-amber-800"> — {t('HR on behalf of the manager', 'الموارد البشرية نيابة عن المدير')}: {dm.signed.reason}</span>
            )}
            {!dm.signed && dm.returned && (
              <span className="text-xs text-rose-700"> {t('returned', 'أُعيد')}: {dm.returned.reason} — {who(dm.returned)}</span>
            )}
          </div>
          <div><span className="text-slate-500">{t('HR approved', 'اعتماد الموارد البشرية')}: </span>{who(ap.hr_approved)}</div>
          <div><span className="text-slate-500">{t('Employee confirmed', 'تأكيد الموظف')}: </span>{who(ap.employee_confirmed)}</div>
          <div><span className="text-slate-500">{t('HR final', 'الاعتماد النهائي')}: </span>{who(ap.hr_final)}</div>
          <div><span className="text-slate-500">{t('Pay released', 'إطلاق الراتب')}: </span>{who(ap.released)}</div>
          {ap.status === 'hr_approved' && ap.review_deadline && (
            <div className="text-xs text-slate-500">{t('Employee review until', 'مهلة مراجعة الموظف حتى')} {ap.review_deadline}</div>
          )}
        </div>

        {pay && (
          <div className="border rounded p-2 space-y-2" data-testid="sheet-pay">
            <div className="flex items-center gap-2 font-semibold">
              {t('Pay', 'الراتب')}
              <span className="text-xs font-normal rounded px-1.5 py-0.5 bg-amber-50 text-amber-800 border border-amber-200">
                {t('Estimate — formula not confirmed by payroll', 'تقديري — المعادلة غير مؤكدة من الرواتب')}
              </span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {[
                ['basic_salary', 'Basic salary', 'الراتب الأساسي'], ['allowances', 'Allowances', 'البدلات'],
                ['hourly_rate', 'Hourly rate', 'أجر الساعة'], ['overtime_pay', 'OT amount', 'مبلغ الإضافي'],
                ['shortfall_deduction', 'Shortfall deduction', 'خصم النقص'], ['absence_deduction', 'Absence deduction', 'خصم الغياب'],
                ['warning_deduction', 'Warning deduction', 'خصم الإنذار'],
                ['estimated_total', 'Total', 'الإجمالي'],
              ].map(([k, en, ar]) => (
                <div key={k} className="border rounded px-2 py-1" data-pay={k}>
                  <div className="text-[11px] text-slate-500">{t(en, ar)}</div>
                  <div className={`font-mono ${k === 'estimated_total' ? 'font-bold' : 'font-semibold'}`}>
                    {pay[k] === null ? <Missing why={missingProfile} /> : `${money(pay[k])}${k === 'estimated_total' ? ` ${pay.currency}` : ''}`}
                  </div>
                </div>
              ))}
            </div>
            {pay.warning_deductions?.length > 0 && (
              <ul className="text-xs" data-testid="sheet-pay-warning-deductions">
                {pay.warning_deductions.map((d) => (
                  <li key={d.reference_no}>
                    {t('Warning deduction', 'خصم الإنذار')} · {d.link
                      ? <a href={d.link} className="text-blue-700 underline print:text-inherit print:no-underline">{d.reference_no}</a>
                      : d.reference_no}
                    {': '}<span className="font-mono">{money(d.total)}</span>
                    {d.days ? ` (${d.amount != null ? `${money(d.amount)} + ` : ''}${d.days} ${t('day(s)', 'يوم')})` : ''}
                  </li>
                ))}
              </ul>
            )}
            <div className="text-[11px] text-slate-500 font-mono" dir="ltr">{pay.formula}</div>
            {pay.missing?.length > 0 && (
              <div className="text-xs text-amber-800">
                {t('Missing in the employee profile: ', 'غير موجود في ملف الموظف: ')}
                {pay.missing.map((m) => (FIELD_LABEL[m] ? FIELD_LABEL[m][isRTL ? 1 : 0] : m)).join('، ')}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
